import React, { useCallback, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { isEmptyField, isInvalidEmail } from '../../../utils';
import { useToast, useSession } from '../../../hooks';
import { resetPassword } from '../../../services/AuthService';
import { Button, InputPassword } from '../../../components';
import { AuthLayout } from '../../../components/layouts';
import { CheckPassword } from './components';
import { CheckPasswordRow, Container } from './styles';

type FormData = {
    email?: string;
    password?: string;
    newPassword?: string;
    newPasswordAgain?: string;
}

type FormError = {
    field: string;
    message: string;
}

const ResetPasswordPage: React.FC = () => {
    const { state } = useLocation() as {
        state: {
            username?: string;
            password?: string;
        } | null;
    };
    const navigate = useNavigate();
    const { showToast } = useToast();
    const { handleRequest } = useSession();
    const [data, setData] = useState<FormData>({
        email: state?.username,
        password: state?.password
    });
    const [passwordCheck, setPasswordCheck] = useState<{
        hasUppercase: boolean;
        hasLowercase: boolean;
        hasNumber: boolean;
        hasSpecialCharacter: boolean;
        hasCorrectPasswordLength: boolean;
    }>({
        hasUppercase: false,
        hasLowercase: false,
        hasNumber: false,
        hasSpecialCharacter: false,
        hasCorrectPasswordLength: false
    });
    const [errors, setErrors] = useState<FormError[]>([]);

    const handleChange = useCallback((newData: FormData) => setData(oldData => ({
        ...oldData,
        ...newData
    })), []);

    const handleChangePassword = useCallback((password: string) => setPasswordCheck({
        hasUppercase: /[A-Z]/.test(password),
        hasLowercase: /[a-z]/.test(password),
        hasNumber: /[0-9]/.test(password),
        hasSpecialCharacter: /[@$#_!%*?&]/.test(password),
        hasCorrectPasswordLength: password.length >= 8 && password.length <= 16
    }), []);

    const validateData = useCallback(() => {
        const { email, password, newPassword, newPasswordAgain } = data;

        setErrors([]);

        let error: boolean = false;

        if (isEmptyField(email)) {
            showToast({
                type: 'error',
                message: 'E-mail obrigatório'
            });

            return true;
        }

        if (isInvalidEmail(email)) {
            showToast({
                type: 'error',
                message: 'E-mail inválido'
            });

            return true;
        }

        if (isEmptyField(password)) {
            setErrors(oldErrors => [...oldErrors, {
                field: 'password',
                message: 'Campo obrigatório'
            }]);
            error = true;
        }

        if (isEmptyField(newPassword)) {
            setErrors(oldErrors => [...oldErrors, {
                field: 'newpassword',
                message: 'Campo obrigatório'
            }]);
            error = true;
        }

        const { hasUppercase, hasLowercase, hasNumber, hasSpecialCharacter, hasCorrectPasswordLength } = passwordCheck;

        if (!hasUppercase || !hasLowercase || !hasNumber || !hasSpecialCharacter || !hasCorrectPasswordLength) {
            setErrors(oldErrors => [...oldErrors, {
                field: 'newpassword',
                message: 'A senha não preenche os requisitos de segurança'
            }]);
            error = true;
        }

        if (isEmptyField(newPasswordAgain)) {
            setErrors(oldErrors => [...oldErrors, {
                field: 'newpasswordagain',
                message: 'Campo obrigatório'
            }]);
            error = true;
        }

        if (newPassword !== newPasswordAgain) {
            setErrors(oldErrors => [...oldErrors, {
                field: 'newpasswordagain',
                message: 'Confirmação está diferente'
            }]);
            error = true;
        }

        return error;
    }, [showToast, data, passwordCheck]);

    const handleResetPassword = useCallback(async () => {
        if (!validateData()) {
            const { email, password, newPassword } = data;

            if (!email || !password || !newPassword)
                throw new Error('The states email, password and newPassword cannot be null');

            const response = await handleRequest(async () => {
                await resetPassword({
                    email,
                    password,
                    newPassword
                });
            });

            if (response.success) {
                showToast({
                    type: 'success',
                    message: 'Senha alterada com sucesso'
                });

                navigate('/', {
                    replace: true,
                    state: {
                        email: state?.username
                    }
                });
            }
        }
    }, [navigate, showToast, validateData, handleRequest, state, data]);

    if (!state)
        return <Navigate to="/" replace />;

    return (
        <AuthLayout>
            <Container>
                <form>
                    <CheckPasswordRow>
                        <p>A nova senha de acesso deve conter:</p>
                        <CheckPassword
                            title="Mínimo 8 e máximo 16 caracteres"
                            valid={passwordCheck.hasCorrectPasswordLength}
                        />
                        <CheckPassword
                            title="Uma letra maiúscula"
                            valid={passwordCheck.hasUppercase}
                        />
                        <CheckPassword
                            title="Uma letra minúscula"
                            valid={passwordCheck.hasLowercase}
                        />
                        <CheckPassword
                            title="Um número"
                            valid={passwordCheck.hasNumber}
                        />
                        <CheckPassword
                            title="Um caractere especial (@$#_!%*?&)"
                            valid={passwordCheck.hasSpecialCharacter}
                        />
                    </CheckPasswordRow>
                    {!state.password && (
                        <InputPassword
                            id="resetpassword.password"
                            label="Senha atual"
                            placeholder="Digite sua senha atual"
                            error={errors.filter(({ field }) => field === 'password')[0]?.message}
                            onChange={e => handleChange({
                                password: e.target.value
                            })}
                            autoFocus
                        />
                    )}
                    <InputPassword
                        id="resetpassword.newpassword"
                        label="Nova senha"
                        placeholder="Digite sua nova senha"
                        error={errors.filter(({ field }) => field === 'newpassword')[0]?.message}
                        onChange={e => {
                            const newPassword = e.target.value;

                            handleChange({ newPassword });
                            handleChangePassword(newPassword);
                        }}
                        autoFocus={!!state.password}
                    />
                    <InputPassword
                        id="resetpassword.newpasswordagain"
                        label="Nova senha novamente"
                        placeholder="Digite sua nova senha"
                        error={errors.filter(({ field }) => field === 'newpasswordagain')[0]?.message}
                        onChange={e => handleChange({
                            newPasswordAgain: e.target.value
                        })}
                        onPaste={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    />
                </form>
                <Button
                    id="resetpassword.savebutton"
                    onClick={handleResetPassword}
                >
                    Salvar
                </Button>
            </Container>
        </AuthLayout>
    );
};
export { ResetPasswordPage };